import React, { useEffect, useState } from 'react';
import ReCAPTCHA from "react-google-recaptcha";
import Spinner from 'shared_features/spinner';
import { useLocation, Link } from 'react-router-dom';

import styles from './styles.module.scss';
import { displayErrorAlert } from 'services/alerts';

// Check if we're running locally
const isLocalEnvironment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

// Hide/show chat widget functions
const hideChatWidget = () => {
    if (window.HubSpotConversations) {
        window.HubSpotConversations.widget.remove();
    }
    window.hsConversationsOnReady = window.hsConversationsOnReady || [];
    window.hsConversationsOnReady.push(() => {
        if (window.HubSpotConversations) {
            window.HubSpotConversations.widget.remove();
        }
    });
    const style = document.createElement('style');
    style.id = 'hide-chat-widget-style';
    style.textContent = `#hubspot-messages-iframe-container { display: none !important; visibility: hidden !important; }`;
    document.head.appendChild(style);
};

const showChatWidget = () => {
    if (window.HubSpotConversations) {
        window.HubSpotConversations.widget.load();
    }
    const style = document.getElementById('hide-chat-widget-style');
    if (style) {
        style.remove();
    }
};

// Check if we're on mobile
const isMobile = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    return /iPad|iPhone|iPod|android/i.test(userAgent);
};

// Email validation
const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

// Mobile Registration Component
const MobileRegister = (props) => {
    const [step, setStep] = useState(1);
    const [termsAgreed, setTermsAgreed] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [firstNameError, setFirstNameError] = useState('');
    const [lastNameError, setLastNameError] = useState('');
    const [hasProgramId, setHasProgramId] = useState(false);
    const recaptchaRef = React.useRef();
    const location = useLocation();

    // Reset register state and hide chat widget on mount
    useEffect(() => {
        // Reset any previous registration state
        if (props.resetRegister) {
            props.resetRegister();
        }

        hideChatWidget();

        const query = new URLSearchParams(location.search);
        const emailParam = query.get('email');
        const inviteParam = query.get('invite');
        const programIdParam = query.get('program_id');

        if (emailParam && emailParam !== props.email) {
            props.updateEmail(emailParam);
        }

        if (inviteParam) {
            localStorage.setItem('pendingInviteCode', inviteParam);
        }

        if (programIdParam) {
            localStorage.setItem('pendingProgramId', programIdParam);
            setHasProgramId(true);
        } else if (localStorage.getItem('pendingProgramId')) {
            setHasProgramId(true);
        }

        return () => {
            showChatWidget();
        };
    }, []);

    const handleContinueToName = () => {
        if (!props.email) {
            setEmailError('Not a valid email');
            return;
        }
        if (!isValidEmail(props.email)) {
            setEmailError('Not a valid email');
            return;
        }
        setEmailError('');
        setStep(2);
    };

    const handleContinueToOrganization = () => {
        let hasError = false;
        if (!firstName.trim()) {
            setFirstNameError('Required');
            hasError = true;
        } else {
            setFirstNameError('');
        }
        if (!lastName.trim()) {
            setLastNameError('Required');
            hasError = true;
        } else {
            setLastNameError('');
        }
        if (hasError) return;
        setStep(3);
    };

    const handleContinueToPassword = () => {
        setStep(4);
    };

    const handleCreateAccount = async () => {
        // Validate password
        if (!props.password || props.password.length < 8) {
            setPasswordError('Must be 8 characters');
            return;
        }
        setPasswordError('');

        // Check terms
        if (!termsAgreed) {
            setShowTermsModal(true);
            return;
        }

        // Proceed with registration
        let token = 'bypass_token_for_development';

        if (!isLocalEnvironment && recaptchaRef.current) {
            try {
                token = await recaptchaRef.current.executeAsync();
            } catch (error) {
                console.error('ReCAPTCHA failed, using bypass token:', error);
            }
        }

        const inviteCode = localStorage.getItem('pendingInviteCode');
        const programId = localStorage.getItem('pendingProgramId');
        const orgName = props.organization_name;
        // Pass firstName and lastName if coming from program page
        props.register(token, props.email, props.password, orgName, props.beta, inviteCode, firstName || null, null, lastName || null, programId);

        if (!isLocalEnvironment && recaptchaRef.current) {
            try {
                recaptchaRef.current.reset();
            } catch (error) {
                console.error('Failed to reset reCAPTCHA:', error);
            }
        }

        // Clear the program_id from localStorage after registration attempt
        if (programId) {
            localStorage.removeItem('pendingProgramId');
        }
    };

    // Success state
    if (props.registered) {
        if (props.is_active) {
            // Account is auto-approved - show signing in message while auto-sign-in happens
            return (
                <div className={styles.mobileContainer}>
                    <div className={styles.mobileHeader}>
                        <img src="https://assets.reponestrength.com/white_logo.png" className={styles.mobileLogo} alt="RepOne" />
                        <h1 className={styles.mobileTitle}>Account Created!</h1>
                    </div>
                    <div className={styles.mobileContent}>
                        <p className={styles.mobileSuccessText}>Your RepOne account has been created successfully!</p>
                        <p className={styles.mobileSuccessText}>Signing you in...</p>
                        <div className={styles.mobileSpinnerContainer}>
                            <img src="https://assets.reponestrength.com/white_logo.png" className={styles.spinnerIcon} alt="" />
                        </div>
                    </div>
                </div>
            );
        } else if (!props.beta) {
            return (
                <div className={styles.mobileContainer}>
                    <div className={styles.mobileHeader}>
                        <img src="https://assets.reponestrength.com/white_logo.png" className={styles.mobileLogo} alt="RepOne" />
                        <h1 className={styles.mobileTitle}>Account Created!</h1>
                    </div>
                    <div className={styles.mobileContent}>
                        <p className={styles.mobileSuccessText}>We're setting up your RepOne account. You'll receive an email when it has been activated.</p>
                    </div>
                </div>
            );
        }
    }

    // Step 1: Email
    if (step === 1) {
        return (
            <div className={styles.mobileContainer}>
                <div className={styles.mobileHeader}>
                    <img src="https://assets.reponestrength.com/white_logo.png" className={styles.mobileLogo} alt="RepOne" />
                    <h1 className={styles.mobileTitle}>Create your<br />RepOne account</h1>
                </div>
                <div className={styles.mobileContent}>
                    <div className={styles.mobileInputGroup}>
                        <div className={styles.mobileLabelRow}>
                            <label className={styles.mobileLabel}>Email</label>
                            {emailError && <span className={styles.mobileError}>{emailError}</span>}
                        </div>
                        <input
                            type="email"
                            className={styles.mobileInput}
                            placeholder="name@domain.com"
                            value={props.email}
                            onChange={(e) => {
                                props.updateEmail(e.target.value);
                                setEmailError('');
                            }}
                            autoFocus
                            autoComplete="email"
                            autoCapitalize="off"
                            autoCorrect="off"
                        />
                    </div>
                    <button
                        className={styles.mobileBorderedButton}
                        onClick={handleContinueToName}
                    >
                        <span>CONTINUE</span>
                        <span className={styles.arrow}>→</span>
                    </button>
                    <Link to="/login" className={styles.mobileUnderlineButton}>
                        I already have an account
                    </Link>
                </div>
            </div>
        );
    }

    // Step 2: Name
    // Historically split out as only worked for program referrals, now is always there
    if (step === 2) {
        return (
            <div className={styles.mobileContainer}>
                <div className={styles.mobileHeader}>
                    <img src="https://assets.reponestrength.com/white_logo.png" className={styles.mobileLogo} alt="RepOne" />
                    <h1 className={styles.mobileTitle}>What's your name?</h1>
                </div>
                <div className={styles.mobileContent}>
                    <div className={styles.mobileInputGroup}>
                        <div className={styles.mobileLabelRow}>
                            <label className={styles.mobileLabel}>First Name</label>
                            {firstNameError && <span className={styles.mobileError}>{firstNameError}</span>}
                        </div>
                        <input
                            type="text"
                            className={styles.mobileInput}
                            placeholder="First name"
                            value={firstName}
                            onChange={(e) => {
                                setFirstName(e.target.value);
                                setFirstNameError('');
                            }}
                            autoFocus
                            autoComplete="given-name"
                            autoCapitalize="words"
                        />
                    </div>
                    <div className={styles.mobileInputGroup}>
                        <div className={styles.mobileLabelRow}>
                            <label className={styles.mobileLabel}>Last Name</label>
                            {lastNameError && <span className={styles.mobileError}>{lastNameError}</span>}
                        </div>
                        <input
                            type="text"
                            className={styles.mobileInput}
                            placeholder="Last name"
                            value={lastName}
                            onChange={(e) => {
                                setLastName(e.target.value);
                                setLastNameError('');
                            }}
                            autoComplete="family-name"
                            autoCapitalize="words"
                        />
                    </div>
                    <button
                        className={styles.mobileBorderedButton}
                        onClick={handleContinueToOrganization}
                    >
                        <span>CONTINUE</span>
                        <span className={styles.arrow}>→</span>
                    </button>
                </div>
            </div>
        );
    }

    // Step 3: Organization
    if (step === 3) {
        return (
            <div className={styles.mobileContainer}>
                <div className={styles.mobileHeader}>
                    <img src="https://assets.reponestrength.com/white_logo.png" className={styles.mobileLogo} alt="RepOne" />
                    <h1 className={styles.mobileTitle}>Create an Organization</h1>
                </div>
                <div className={styles.mobileContent}>
                    <div className={styles.mobileInputGroup}>
                        <div className={styles.mobileLabelRow}>
                            <label className={styles.mobileLabel}>Organization (optional)</label>
                        </div>
                        <input
                            type="text"
                            className={styles.mobileInput}
                            placeholder="Organization Name"
                            value={props.organization_name}
                            onChange={(e) => {
                                props.updateOrganizationName(e.target.value);
                            }}
                            autoComplete="organization"
                            autoCapitalize="words"
                        />
                    </div>
                    <span className={styles.mobileMoreInfo}>
                        Creates a Coaching Organization. You can do this later
                    </span>
                    <button
                        className={styles.mobileBorderedButton}
                        onClick={handleContinueToPassword}
                    >
                        <span>CONTINUE</span>
                        <span className={styles.arrow}>→</span>
                    </button>
                </div>
            </div>
        );
    }

    // Step 4: Password
    return (
        <div className={styles.mobileContainer}>
            <div className={styles.mobileHeader}>
                <img src="https://assets.reponestrength.com/white_logo.png" className={styles.mobileLogo} alt="RepOne" />
                <h1 className={styles.mobileTitle}>Create your<br />RepOne account</h1>
            </div>
            <div className={styles.mobileContent}>
                <div className={styles.mobileInputGroup}>
                    <div className={styles.mobileLabelRow}>
                        <label className={styles.mobileLabel}>Password</label>
                        {passwordError && <span className={styles.mobileError}>{passwordError}</span>}
                    </div>
                    <input
                        type="password"
                        className={styles.mobileInput}
                        placeholder="********"
                        value={props.password}
                        onChange={(e) => {
                            props.updatePassword(e.target.value);
                            setPasswordError('');
                        }}
                        autoFocus
                        autoComplete="new-password"
                    />
                </div>
                <div className={styles.mobileCheckboxGroup}>
                    <label className={styles.mobileCheckboxLabel}>
                        <input
                            type="checkbox"
                            checked={termsAgreed}
                            onChange={(e) => setTermsAgreed(e.target.checked)}
                            className={styles.mobileCheckbox}
                        />
                        <span className={styles.mobileTermsText}>
                            I agree to the{' '}
                            <a
                                href="https://www.reponestrength.com/terms-of-service"
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.mobileTermsLink}
                            >
                                terms and conditions
                            </a>
                        </span>
                    </label>
                </div>
                {!isLocalEnvironment && (
                    <ReCAPTCHA
                        ref={recaptchaRef}
                        size="invisible"
                        sitekey="6LdxPboZAAAAADdtY2YARZm2eOlRlkt5XAebeUgT"
                    />
                )}
                <button
                    className={styles.mobileBorderedButton}
                    onClick={handleCreateAccount}
                    disabled={props.registering}
                >
                    <span>{props.registering ? 'CREATING...' : 'CREATE ACCOUNT'}</span>
                    <span className={styles.arrow}>→</span>
                </button>
            </div>

            {/* Terms Modal */}
            {showTermsModal && (
                <div className={styles.mobileModalOverlay}>
                    <div className={styles.mobileModal}>
                        <p className={styles.mobileModalText}>
                            To create an account you must agree to the terms and conditions
                        </p>
                        <button
                            className={styles.mobileBorderedButton}
                            onClick={() => setShowTermsModal(false)}
                        >
                            <span>CONTINUE</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// Desktop Registration Component (original)
const DesktopRegister = (props) => {
    const recaptchaRef = React.useRef();
    const location = useLocation();
    const [firstName, setFirstName] = useState('');
    const [middleName, setMiddleName] = useState('');
    const [lastName, setLastName] = useState('');

    useEffect(() => {
        // Reset any previous registration state
        if (props.resetRegister) {
            props.resetRegister();
        }

        hideChatWidget();

        const query = new URLSearchParams(location.search);
        const emailParam = query.get('email');
        const inviteParam = query.get('invite');
        const programIdParam = query.get('program_id');

        if (emailParam && emailParam !== props.email) {
            props.updateEmail(emailParam);
        }

        if (inviteParam) {
            localStorage.setItem('pendingInviteCode', inviteParam);
        }

        if (programIdParam) {
            localStorage.setItem('pendingProgramId', programIdParam);
        }

        return () => {
            showChatWidget();
        };
    }, []);

    const onSubmitWithReCAPTCHA = async () => {
        if (!props.beta && !props.organization_name) {
            displayErrorAlert('Organization name is required');
            return;
        }

        let token = 'bypass_token_for_development';

        if (!isLocalEnvironment && recaptchaRef.current) {
            try {
                token = await recaptchaRef.current.executeAsync();
            } catch (error) {
                console.error('ReCAPTCHA failed, using bypass token:', error);
            }
        }

        const inviteCode = localStorage.getItem('pendingInviteCode');
        const programId = localStorage.getItem('pendingProgramId');
        props.register(token, props.email, props.password, props.organization_name, props.beta, inviteCode, firstName || null, middleName || null, lastName || null, programId);

        if (!isLocalEnvironment && recaptchaRef.current) {
            try {
                recaptchaRef.current.reset();
            } catch (error) {
                console.error('Failed to reset reCAPTCHA:', error);
            }
        }

        // Clear the program_id from localStorage after registration attempt
        if (programId) {
            localStorage.removeItem('pendingProgramId');
        }
    }

    if (props.registered) {
        if (props.is_active) {
            // Account is auto-approved - show signing in message while auto-sign-in happens
            return (<div className={styles.success}>
                <h2>Your RepOne account has been created successfully!</h2>
                <p>Signing you in...</p>
                <Spinner />
            </div>);
        } else if (!props.beta) {
            return (<div className={styles.error}>
                <h2>We're setting up your RepOne account, you'll receive an email when it has been activated</h2>
                <p>Tip: Bookmark this address so you can return to the coaching portal with a single click</p>
            </div>);
        }
    }

    let submitText;
    if (props.registering) {
        submitText = '';
    } else if (props.registered) {
        submitText = 'Loading...';
    } else {
        submitText = 'Create Account';
    }

    return (
        <div>
            <div className={styles.main}>
                <img src="https://assets.reponestrength.com/black_logo.png" />
                <div>Create your RepOne Account</div>

                <form className={styles.form} autoComplete={"on"} onSubmit={e => { e.preventDefault() }}>
                    <label>Account *</label>
                    <input type="text"
                        className={styles.textInput}
                        placeholder="Email *"
                        value={props.email}
                        onChange={(event) => props.updateEmail(event.target.value)} />
                    <input type="password"
                        className={styles.textInput}
                        placeholder="Password *"
                        value={props.password}
                        onChange={(event) => props.updatePassword(event.target.value)} />
                    <label>Name *</label>
                    <input type="text"
                        className={styles.textInput}
                        placeholder="First name *"
                        value={firstName}
                        onChange={(event) => setFirstName(event.target.value)} />
                    <input type="text"
                        className={styles.textInput}
                        placeholder="Middle name"
                        value={middleName}
                        onChange={(event) => setMiddleName(event.target.value)} />
                    <input type="text"
                        className={styles.textInput}
                        placeholder="Last name *"
                        value={lastName}
                        onChange={(event) => setLastName(event.target.value)} />
                    <label>{props.beta ? 'Coach Organization (can be done later)' : 'Organization *'}</label>
                    <input type="text"
                        className={styles.textInput}
                        placeholder={props.beta ? `Organization Name` : 'Organization Name *'}
                        value={props.organization_name}
                        onChange={(event) => props.updateOrganizationName(event.target.value)} />
                    {!isLocalEnvironment && (
                        <ReCAPTCHA
                            ref={recaptchaRef}
                            size="invisible"
                            sitekey="6LdxPboZAAAAADdtY2YARZm2eOlRlkt5XAebeUgT"
                        />
                    )}
                    <div className={styles.buttonsContainer}>
                        <div className={styles.submitButton}>
                            <input className={styles.submit} type="submit" value={submitText} onClick={onSubmitWithReCAPTCHA} disabled={props.registering || props.registered} />
                            {props.registering ? <div className={styles.spinnerContainer}><Spinner /></div> : null}
                        </div>
                        {props.beta && <Link
                            to={`/login`}
                            className={styles.loginButton}
                        >
                            Login
                        </Link>}
                    </div>
                </form>
            </div>
        </div>
    );
};

// Main component that switches between mobile and desktop
export default props => {
    if (isMobile()) {
        return <MobileRegister {...props} />;
    }
    return <DesktopRegister {...props} />;
};
